import { gql } from '@apollo/client';

// Kick off generation of the first prompt output. Returns the conversation id
// + status so the UI can poll ASK_ADDY_CONVERSATION_QUERY until it's ready.
export const START_ASK_ADDY_CONVERSATION = gql`
  mutation StartAskAddyConversation(
    $packageId: ID!
    $promptType: AskAddyPromptType!
    $inputs: JSON
    $force: Boolean
  ) {
    startAskAddyConversation(
      packageId: $packageId
      promptType: $promptType
      inputs: $inputs
      force: $force
    ) {
      id
      status
      promptType
    }
  }
`;

// Poll for status + read messages[0] (the first output) once ready.
export const ASK_ADDY_CONVERSATION_QUERY = gql`
  query AskAddyConversation($id: ID!) {
    askAddyConversation(id: $id) {
      id
      promptType
      status
      error
      inputs
      dataPackKey
      outputs {
        kind
        s3Key
      }
      messages {
        role
        content
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

// List the user's past Ask Addy requests for the "My Work → Ask Addy" tab, one
// row per { Profile Report, prompt } combo. packageId + promptType drive the
// re-open-and-restore link into Ask Addy.
export const ASK_ADDY_CONVERSATIONS_QUERY = gql`
  query AskAddyConversations {
    askAddyConversations {
      id
      packageId
      reportName
      promptType
      status
      hasOutput
      updatedAt
    }
  }
`;

// Fetch a short-lived presigned URL for a conversation's generated output
// (e.g. the exec-summary PDF). Lazy-fetched on the Download click so the link
// is fresh each time; mirrors the existing purchasedInsight download pattern.
export const ASK_ADDY_OUTPUT_URL_QUERY = gql`
  query AskAddyOutputUrl($conversationId: ID!, $kind: String) {
    askAddyOutputUrl(conversationId: $conversationId, kind: $kind) {
      downloadLink
    }
  }
`;

// ---------------------------------------------------------------------------
// Chat streaming (SSE) — lives outside GraphQL.
// ---------------------------------------------------------------------------

// VITE_APOLLO_CLIENT_URI points at the GraphQL endpoint (…/graphql). The chat
// route is a sibling REST route (…/api/ask-addy/…), so derive the origin.
function apiOrigin(): string {
  const uri = import.meta.env.VITE_APOLLO_CLIENT_URI as string;
  try {
    return new URL(uri).origin;
  } catch {
    // Relative URI (same-origin dev through nginx).
    return window.location.origin;
  }
}

export interface StreamCallbacks {
  onDelta: (text: string) => void;
  onDone?: () => void;
  onError?: (message: string) => void;
}

/**
 * POST a follow-up message and stream the assistant's reply token-by-token via
 * Server-Sent Events. Auth uses the same cookie credentials as the rest of the
 * app (the server's JWT middleware falls back to the refreshToken cookie); an
 * optional bearer token is sent when available.
 *
 * Returns a function that aborts the in-flight stream.
 */
export function streamAskAddyMessage(
  conversationId: string,
  message: string,
  { onDelta, onDone, onError }: StreamCallbacks,
  token?: string
): () => void {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(
        `${apiOrigin()}/api/ask-addy/${conversationId}/message`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ message }),
          signal: controller.signal,
        }
      );

      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        onError?.(text || `Request failed (${res.status}).`);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      // Parse the SSE frames as they arrive. Each event is separated by a blank
      // line; we care about default `data:` frames (deltas), `event: done`, and
      // `event: error`.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          let eventName = 'message';
          let dataLine = '';
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
          }
          if (!dataLine) continue;

          if (eventName === 'error') {
            try {
              onError?.(JSON.parse(dataLine).error || 'Streaming error.');
            } catch {
              onError?.('Streaming error.');
            }
            return;
          }
          if (eventName === 'done') {
            onDone?.();
            return;
          }
          try {
            const { delta } = JSON.parse(dataLine);
            if (delta) onDelta(delta);
          } catch {
            // ignore malformed frame
          }
        }
      }
      onDone?.();
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        onError?.((err as Error).message || 'Streaming failed.');
      }
    }
  })();

  return () => controller.abort();
}
